import React from 'react';
import styled from 'styled-components';
import { AppColors } from '../../../StyledComponents/Graphics';
import { formatDateTime } from '../../../../Controllers/utils';
import { OpenArrowRightIcon } from '../../../Icons/Icons';

const ArticleContainer = styled.a`
  display: block;
  padding: 12px 0;
  text-decoration: none;
  color: inherit;
  background-color: transparent;
  border-bottom: 1px solid ${AppColors.MRNAVY40};
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-bottom-color: ${AppColors.MRNAVY50};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: 10px 0;
  }
`;

const HeadlineContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    margin-bottom: 5px;
  }
`;

const FaviconContainer = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Favicon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 50%;
`;

const FaviconFallback = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${AppColors.MRNAVY50};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: ${AppColors.MRWHITE};
`;

const Headline = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: ${AppColors.MRWHITE};
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Excerpt = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${AppColors.MRWHITE};
  opacity: 0.8;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 13px;
    -webkit-line-clamp: 3;
  }
`;

const Metadata = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  color: ${AppColors.MRGREY};

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const Source = styled.span`
  font-weight: 500;
  color: ${AppColors.MRWHITE};
  opacity: 0.6;
`;

const Timestamp = styled.span`
  color: ${AppColors.MRWHITE};
  opacity: 0.6;

  &::before {
    content: '•';
    margin: 0 8px;
  }

  @media (max-width: 768px) {
    &::before {
      margin: 0 6px;
    }
  }
`;

const ExternalLinkIndicator = styled(OpenArrowRightIcon)`
  margin-left: auto;
  opacity: 0.4;
  transition: opacity 0.2s ease;
  float: none;
  height: 16px;
  width: 16px;
  transform: rotate(-45deg);

  path {
    fill: ${AppColors.MRWHITE};
  }

  ${ArticleContainer}:hover & {
    opacity: 0.7;
  }
`;

export const ArticleLink = ({ article }) => {
  const { link, source, excerpt, headline, timestamp, favicon_url } = article;

  const getSourceInitials = (sourceName) => {
    if (!sourceName) return '?';
    const words = sourceName.split(' ');
    if (words.length === 1) return sourceName.substring(0, 2).toUpperCase();
    return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
  };

  const [faviconError, setFaviconError] = React.useState(false);

  return (
    <ArticleContainer
      href={link}
      target="_blank"
      rel="noopener noreferrer"
    >
      <HeadlineContainer>
        <FaviconContainer>
          {favicon_url && !faviconError ? (
            <Favicon
              src={favicon_url}
              alt={source}
              onError={() => setFaviconError(true)}
            />
          ) : (
            <FaviconFallback>
              {getSourceInitials(source)}
            </FaviconFallback>
          )}
        </FaviconContainer>
        <Headline>{headline}</Headline>
      </HeadlineContainer>
      {excerpt && <Excerpt>{excerpt}</Excerpt>}
      <Metadata>
        <Source>{source}</Source>
        {timestamp && <Timestamp>{formatDateTime(timestamp)}</Timestamp>}
        <ExternalLinkIndicator />
      </Metadata>
    </ArticleContainer>
  );
};
